/**
 * Prompt scene — left-contained question card with countdown ring.
 *
 * Renders the surveyed prompt (e.g. "Name something you find in a kitchen")
 * and a circular countdown that activates after a short read-window. The
 * content is constrained to MAX_W (~1180px) and pinned to the left of the
 * stage so the right-side leaderboard panel stays untouched.
 */

import type { QuestionState } from '../state/types.js';

export interface PromptHandle {
  el: HTMLDivElement;
  setQuestion(q: QuestionState): void;
  setVisible(v: boolean): void;
  tick(): void;
}

const MAX_W = 1180;

export function createPrompt(stage: HTMLElement): PromptHandle {
  const el = document.createElement('div');
  el.setAttribute('data-scene', 'prompt');
  el.setAttribute('data-testid', 'ss-prompt');
  el.style.cssText = `
    position: absolute; inset: 0;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    background: linear-gradient(180deg, var(--sg-bg, #0A0E27) 0%, var(--sg-secondary, #060CE9) 100%);
    pointer-events: none;
  `;

  // Inner container: centered visually within the LEFT content band (0..1480px,
  // since the right-side leaderboard panel sits at x=1500..1880). The 1180px
  // box is offset by 80px from the left edge so content has breathing room
  // from the screen edge while keeping the 100px gutter before the leaderboard.
  // Children are align-items: center so the question card sits at the visual
  // center of the 1180 zone (~x=670 in stage coords).
  const inner = document.createElement('div');
  inner.setAttribute('data-testid', 'ss-prompt-inner');
  inner.style.cssText = `
    position: absolute;
    top: 0; left: 80px;
    width: ${MAX_W}px;
    max-width: ${MAX_W}px;
    height: 100%;
    padding: 80px 40px;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center;
    box-sizing: border-box;
  `;
  el.appendChild(inner);

  const eyebrow = document.createElement('div');
  eyebrow.setAttribute('data-testid', 'ss-prompt-eyebrow');
  eyebrow.style.cssText = 'font-size: 36px; letter-spacing: 8px; opacity: 0.85;';

  const promptEl = document.createElement('div');
  promptEl.style.cssText = `
    margin-top: 24px;
    font-size: 84px; line-height: 1.05;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 6px 0 var(--sg-secondary, #060CE9);
    max-width: 1080px;
  `;
  promptEl.setAttribute('data-testid', 'ss-prompt-text');

  const cta = document.createElement('div');
  cta.style.cssText = 'margin-top: 32px; font-size: 30px; opacity: 0.9;';
  cta.textContent = 'Type your guess in chat — one shot per player!';

  const ring = document.createElement('div');
  ring.style.cssText = `
    margin-top: 36px;
    font-family: 'Inter', sans-serif;
    font-size: 60px;
    color: var(--sg-primary, #FFC43D);
    background: rgba(0,0,0,0.3);
    border: 4px solid var(--sg-primary, #FFC43D);
    border-radius: 999px;
    width: 150px; height: 150px;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700;
  `;
  ring.setAttribute('data-testid', 'ss-prompt-countdown');

  inner.appendChild(eyebrow);
  inner.appendChild(promptEl);
  inner.appendChild(cta);
  inner.appendChild(ring);
  stage.appendChild(el);

  let cur: QuestionState | null = null;

  function update(): void {
    if (!cur) return;
    // Continuous mode (total === 0) hides the "of M" suffix.
    eyebrow.textContent = cur.total > 0
      ? `QUESTION ${cur.index + 1} OF ${cur.total}`
      : `QUESTION ${cur.index + 1}`;
    promptEl.textContent = cur.prompt;
    const now = Date.now();
    if (now < cur.countdownStartsAtMs) {
      ring.textContent = 'GO!';
      ring.style.color = '#fff';
    } else {
      const remaining = Math.max(0, Math.ceil((cur.endsAtMs - now) / 1000));
      ring.textContent = String(remaining);
      ring.style.color = remaining <= 5 ? '#ef4444' : 'var(--sg-primary, #FFC43D)';
    }
  }

  return {
    el,
    setQuestion(q) {
      cur = q;
      update();
    },
    setVisible(v) {
      el.style.display = v ? 'block' : 'none';
    },
    tick() {
      update();
    },
  };
}
